import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import {
  getUserActivity,
  resetUserActivityLastBrowsedSection,
} from '../../redux/activitySlice'
import { useAppHistory } from '../../utils/useAppHistory'
import { useIsDesktop } from '../../utils/useBreakpoint'
import { BackButton } from '../ui/ActionButtons'
import { Page } from '../ui/PageTemplate'
import { createActivityDiary } from './ActivityDiary'
import DiaryEntry from './DiaryEntry'
import SkeletonLoader from './SkeletonLoader'
import TypesAndPlaces from './TypesAndPlaces'

const AtLeastAsLongAsSelectMenu = styled.div`
  min-height: 300px;
`

const UserActivityContent = ({ changes, userId }) => {
  const { userActivityLastBrowsedSection } = useSelector(
    (state) => state.activity,
  )
  const loadMoreRef = useRef()
  const [displayLimit, setDisplayLimit] = useState(
    userActivityLastBrowsedSection.displayLimit || 100,
  )

  const needsLoadMore = changes?.length > displayLimit

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setDisplayLimit((x) => x + 20)
        }
      },
      { threshold: 1.0 },
    )

    const currentRef = loadMoreRef.current

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [needsLoadMore, displayLimit])

  return (
    <>
      <AtLeastAsLongAsSelectMenu>
        {changes.slice(0, displayLimit).map((entry) => (
          <DiaryEntry
            key={entry.formattedDate}
            entry={entry}
            userId={userId}
            displayLimit={displayLimit}
          />
        ))}
      </AtLeastAsLongAsSelectMenu>

      {needsLoadMore && <div ref={loadMoreRef}></div>}
    </>
  )
}

const StyledBackButton = styled(BackButton)`
  margin-bottom: 23px;
`

const UserActivityPage = () => {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  let { userId } = useParams()
  userId = parseInt(userId)
  const history = useAppHistory()
  const isDesktop = useIsDesktop()

  const { changesByUser, userActivityLastBrowsedSection } = useSelector(
    (state) => state.activity,
  )
  const changes = changesByUser[userId]

  const { t, i18n } = useTranslation()

  const { typesAccess } = useSelector((state) => state.type)

  const changesReady = !typesAccess.isEmpty

  const isCurrentUser = userId === user?.id
  const isLoading = changes === undefined

  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectedPlaces, setSelectedPlaces] = useState([])

  const activityDiary = useMemo(
    () => createActivityDiary(changes || [], typesAccess, t, i18n.language),
    [changes, typesAccess, t, i18n.language],
  )

  // Get filtered entries from the activity diary
  const filteredEntries = useMemo(
    () => activityDiary.getFilteredEntries(selectedTypes, selectedPlaces),
    [activityDiary, selectedTypes, selectedPlaces],
  )

  useEffect(() => {
    if (userActivityLastBrowsedSection.id) {
      const periodElement = document.getElementById(
        `${userActivityLastBrowsedSection.id}`,
      )
      if (periodElement && userActivityLastBrowsedSection.userId === userId) {
        periodElement.scrollIntoView()
      }
      dispatch(resetUserActivityLastBrowsedSection())
    }
  }, [userActivityLastBrowsedSection, dispatch, userId])

  useEffect(() => {
    if (changesReady) {
      dispatch(getUserActivity(userId, isCurrentUser)).then((action) => {
        if (action.error) {
          history.push('/map')
          toast.error(
            t('error_message.api.fetch_location_changes_failed', {
              message: action.error.message || t('error_message.unknown_error'),
            }),
          )
        }
      })
    }
  }, [dispatch, changesReady, userId]) //eslint-disable-line

  const userName =
    changes?.length > 0 && changes[0].author ? changes[0].author : `#${userId}`

  const showBackButton = !(isDesktop && isCurrentUser)

  return (
    <Page>
      {showBackButton && (
        <StyledBackButton
          backPath={isCurrentUser ? '/account/edit' : `/users/${userId}`}
        />
      )}

      {isLoading ? (
        <Skeleton width="15em" height={30} style={{ marginBottom: '1em' }} />
      ) : (
        <h2>
          {isCurrentUser ? (
            t('users.your_activity')
          ) : (
            <>
              {t('glossary.activity')}: {userName}
            </>
          )}
        </h2>
      )}

      <TypesAndPlaces
        typeCounts={
          isLoading ? [] : activityDiary.calculateTypeCounts(selectedPlaces)
        }
        cityCounts={
          isLoading ? [] : activityDiary.calculateCityCounts(selectedTypes)
        }
        selectedTypes={selectedTypes}
        selectedPlaces={selectedPlaces}
        onTypeChange={setSelectedTypes}
        onPlaceChange={setSelectedPlaces}
        isDisabled={isLoading}
      />

      {isLoading && <SkeletonLoader count={5} />}

      {!isLoading &&
        (changes.length > 0 ? (
          <UserActivityContent changes={filteredEntries} userId={userId} />
        ) : (
          <p>
            {isCurrentUser
              ? t('pages.changes.your_activity_empty')
              : t('pages.changes.user_activity_empty')}
          </p>
        ))}
    </Page>
  )
}

export default UserActivityPage
